/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  defaultSpeedLimit,
  ExtendedDateFormat,
  ISODateTimeFormat,
  ISODateTimeStartOfDayFormat,
  meterBasedSensors,
  PrimarySensorTypes,
} from '@app/shared/constants';
import {
  GasSensorClassification,
  MeasurementTypes,
  SensorClassification,
  SensorDataChartTypes,
  SensorTypes,
  SpeedSensorClassification,
} from '@app/shared/enums';
import { GqlQueries } from '@app/shared/graphql-queries';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { StundenwerteService } from '@app/shared/services/visualization/stundenwerte.service';
import { ViertelstundenwerteService } from '@app/shared/services/visualization/viertelstundenwerte.service';
import { StringUtility } from '@app/shared/string-utility';
import { environment } from '@env/environment';
import { assign, chain, clone, first, flattenDeep, isEmpty, isNil, map as _map, maxBy, meanBy, orderBy, range, sum, uniq } from 'lodash';
import * as moment from 'moment';
import * as momenttz from 'moment-timezone';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DeviceTypeMeasurementTypeMappers, DeviceTypes } from '../models/device-types';
import { Devices, DevicesWithCountTrue } from '../models/devices';
import { FilteredBuildingTypes } from '../models/filtered-building-types';
import { LibChart } from '../models/lib-chart-model';
import { QualityIndex } from '../models/quaility-index';
import { Sensor, SensorReading, TableMetadata } from '../models/sensor';
import {
  OrderedSensorPropertiesReadings,
  SensorByIDLocationContext,
  SensorDetailsBySensorId,
  SensorPropertiesReadings,
  SpanView,
} from '../models/sensor-by-id';
import { SensorFilter } from '../models/sensor-filter';
import { SensorReadingsFromYear } from '../models/sensor-readings-from-year';
import { SensorsByMunicipalityNodes, TransformedNode } from '../models/sensors-by-municipality-nodes';
import { SpeedLimitDto } from '../models/speed-limit';
import { Thresholds } from '../models/thresholds';
import { SensorDataUtility } from '../sensor-data-utility';
import { GraphQlService } from './graphql.service';
import { SensorDataHelperService } from './sensor-data-helper.service';
import { SensorReadingState } from '../models/sensor-reading-state';
import { SensorTypeDataStoreService } from './sensor-type-data-store.service';
import { SensorTableNodes, SensorReadingOverviewNodes } from '../models/sensor-overview';
import { IoTReadings } from '../models/iot-readings';
import { QualityIndexDto } from '../models/quality-index-payload';

@Injectable({
  providedIn: 'root',
})
export class SensorDataService extends GraphQlService {
  private _filteredBuildingTypes: BehaviorSubject<FilteredBuildingTypes[]> = new BehaviorSubject<FilteredBuildingTypes[]>([]);
  private sensorMetaData: Sensor | null = null;
  public readonly filteredBuildingTypes$ = this._filteredBuildingTypes.asObservable();

  constructor(
    public http: HttpClient,
    public sensorTypeDataStoreService: SensorTypeDataStoreService,
    private helper: SensorDataHelperService,
    private viertelStundenwerteService: ViertelstundenwerteService,
    private stundenwerteService: StundenwerteService,
    private userMunicipalityService: UserMunicipalitiesService
  ) {
    super(http);
    this.helper.translateText();
  }

  private parseFifteenMinuteData(selectedDate: Date, sensorType: string, classification: string, readings: IoTReadings[]): void {
    if (readings.length === 96 || isEmpty(readings)) {
      return;
    }

    for (let index = 0; index < 96; index++) {
      const addedSelectedDate = moment(selectedDate)
        .add(15 * index, 'minutes')
        .format(ISODateTimeFormat);
      if (!readings.some((reading: IoTReadings) => StringUtility.areStringsIdentical(moment(reading.date).format(ISODateTimeFormat), addedSelectedDate))) {
        const missingReadings = {
          date: addedSelectedDate,
          day: 0,
          hour: moment(addedSelectedDate).hour(),
          month: 0,
          year: 0,
        } as IoTReadings;
        if (StringUtility.areStringsIdentical(sensorType, SensorTypes.ELECTRICITY)) {
          readings.push({
            ...missingReadings,
            el_consumption_total_1: null,
            el_consumption_obis_180_1: null,
          });
        } else if (StringUtility.areStringsIdentical(sensorType, SensorTypes.GAS)) {
          if (StringUtility.areStringsIdentical(classification, GasSensorClassification.ESTIMATED)) {
            readings.push({
              ...missingReadings,
              gas_consumption_obis_converted_estimation_1: null,
            });
          } else {
            readings.push({
              ...missingReadings,
              gas_consumption_obis_raw_1: null,
            });
          }
        }
      }
    }
  }

  public getFifteenMinuteData(
    sensorId: string | number,
    date: Date,
    timeIntervals: string[],
    classification: string,
    isHourly: boolean = false,
    sensorType: string = SensorTypes.TEMPERATURE
  ): Observable<OrderedSensorPropertiesReadings> {
    const filters = `dayView: { date: "${moment(date).format(ExtendedDateFormat)}", offset_UTC: "Europe/Berlin"}`;

    const datasetLabels = this.helper.getDataSetLabels(sensorType, classification);
    const properties = Object.getOwnPropertyNames(datasetLabels);
    return this.getSensorReadings(sensorId, filters, sensorType, classification).pipe(
      map((resp: OrderedSensorPropertiesReadings): OrderedSensorPropertiesReadings => {
        let readings = resp.data.aggregateSensors.sensors;

        if (StringUtility.hasAnyMatchInArray(meterBasedSensors, [sensorType.trim().toUpperCase()])) {
          this.parseFifteenMinuteData(date, sensorType, classification, readings);
        }

        readings = orderBy(readings, (reading: IoTReadings) => moment(reading.date).toDate());
        const newReadings: IoTReadings[] = [];
        timeIntervals.forEach((timeString: string) => {
          if (
            !isHourly &&
            StringUtility.areStringsIdentical(sensorType, SensorTypes.SPEED) &&
            StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT) &&
            StringUtility.areStringsIdentical(timeString, '00:00')
          ) {
            timeString = '24:00';
          }

          const runningEndTime = `${moment(date).format(ExtendedDateFormat)}T${timeString}:00`;

          const runningStartTime = isHourly
            ? `${moment(runningEndTime).subtract(1, 'hours').format(ISODateTimeFormat)}`
            : `${moment(runningEndTime).subtract(15, 'minutes').format(ISODateTimeFormat)}`;

          const runningTimeReadings = readings.filter((reading: IoTReadings) =>
            moment(reading.date).isBetween(runningStartTime, runningEndTime, 'minutes', isHourly ? '[)' : '(]')
          );

          const readingByHourlyOrByFifteenMinute = isHourly
            ? runningTimeReadings.find((start: IoTReadings) => start.date === runningStartTime)
            : runningTimeReadings.find((end: IoTReadings) => end.date === runningEndTime);

          if (isNil(readingByHourlyOrByFifteenMinute)) {
            const reading = clone(first(readings))!;
            reading.date = runningEndTime;
            properties.forEach((prop: string) => (reading[prop] = null));

            if (
              StringUtility.areStringsIdentical(sensorType, SensorTypes.SPEED) &&
              StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT)
            ) {
              reading.speed_avg_1 = 0;
              reading.speed_max_1 = 0;
              reading.cars_zone_7_count = null;
              reading.cars_zone_30_count = null;
              reading.cars_zone_50_count = null;
              reading.cars_zone_60_count = null;
              reading.cars_zone_70_count = null;
              reading.cars_zone_80_count = null;
            }
            newReadings.push(reading);
          } else {
            properties.forEach(
              (prop: string) => (readingByHourlyOrByFifteenMinute[prop] = sum(runningTimeReadings.map((reading: IoTReadings) => Number(reading[prop]))))
            );

            if (
              StringUtility.areStringsIdentical(sensorType, SensorTypes.SPEED) &&
              StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT) &&
              isHourly
            ) {
              const avgSpeed = meanBy(runningTimeReadings, 'speed_avg_1');
              const maxSpeed = maxBy(runningTimeReadings, 'speed_max_1')?.['speed_max_1'] ?? null;
              readingByHourlyOrByFifteenMinute.speed_avg_1 = avgSpeed;
              readingByHourlyOrByFifteenMinute.speed_max_1 = maxSpeed;
            }
            newReadings.push(readingByHourlyOrByFifteenMinute);
          }
        });

        resp.data.aggregateSensors.sensors = orderBy(newReadings, (reading: IoTReadings) => moment(reading.date).toDate());
        return resp;
      })
    );
  }

  public getDailyInAMonthData(
    sensorId: string | number,
    month: number,
    year: number,
    days: number[],
    classification: string,
    sensorType: string = SensorTypes.TEMPERATURE
  ): Observable<OrderedSensorPropertiesReadings> {
    const filters = `monthView : {year: ${year}, month: ${month + 1}, offset_UTC: "Europe/Berlin"}`;
    const datasetLabels = this.helper.getDataSetLabels(sensorType, classification);
    const properties = Object.getOwnPropertyNames(datasetLabels);

    return this.getSensorReadings(sensorId, filters, sensorType, classification).pipe(
      map((resp: OrderedSensorPropertiesReadings): OrderedSensorPropertiesReadings => {
        const mappedReadings: IoTReadings[] = [];
        const readings = this.convertReadingsToLocalTime(resp.data.aggregateSensors.sensors);
        days.forEach((day: number) => {
          const runningDate = moment(new Date(year, month, day)).format(ExtendedDateFormat);
          const readingByDay = readings.find((reading: IoTReadings) => moment(reading.date).format(ExtendedDateFormat) === runningDate);
          if (isNil(readingByDay)) {
            const emptyReading = clone(first(readings))!;

            emptyReading.day = day;
            emptyReading.date = `${moment(runningDate).format(ISODateTimeStartOfDayFormat)}`;

            properties.forEach((prop: string) => {
              emptyReading[prop] = null;
            });

            if (
              StringUtility.areStringsIdentical(sensorType, SensorTypes.SPEED) &&
              StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT)
            ) {
              emptyReading.cars_zone_7_count = null;
              emptyReading.cars_zone_30_count = null;
              emptyReading.cars_zone_50_count = null;
              emptyReading.cars_zone_60_count = null;
              emptyReading.cars_zone_70_count = null;
              emptyReading.cars_zone_80_count = null;
            }
            mappedReadings.push(emptyReading);
          } else {
            readingByDay.day = day;
            mappedReadings.push(readingByDay);
          }
        });
        resp.data.aggregateSensors.sensors = orderBy(mappedReadings, (reading: IoTReadings) => reading.day);
        return resp;
      })
    );
  }

  public getMonthlyInAYearData(
    sensorId: string | number,
    year: number,
    months: number[],
    classification: string,
    sensorType: string = SensorTypes.TEMPERATURE
  ): Observable<OrderedSensorPropertiesReadings> {
    const filters = `yearmonthView:{ year: ${year}, offset_UTC: "Europe/Berlin"}`;
    const datasetLabels = this.helper.getDataSetLabels(sensorType, classification);
    const properties = Object.getOwnPropertyNames(datasetLabels);

    return this.getSensorReadings(sensorId, filters, sensorType, classification).pipe(
      map((resp: OrderedSensorPropertiesReadings): OrderedSensorPropertiesReadings => {
        const readings = resp.data.aggregateSensors.sensors;
        months.forEach((month: number) => {
          if (isNil(readings.find((reading: IoTReadings) => reading.month === month))) {
            const reading = clone(first(readings))!;
            reading.month = month;

            properties.forEach((prop: string) => {
              reading[prop] = null;
            });

            if (
              StringUtility.areStringsIdentical(sensorType, SensorTypes.SPEED) &&
              StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT)
            ) {
              reading.cars_zone_7_count = null;
              reading.cars_zone_30_count = null;
              reading.cars_zone_50_count = null;
              reading.cars_zone_60_count = null;
              reading.cars_zone_70_count = null;
              reading.cars_zone_80_count = null;
            }

            readings.push(reading);
          }
        });
        resp.data.aggregateSensors.sensors = orderBy(readings, (reading: IoTReadings) => reading.month);
        return resp;
      })
    );
  }

  public getDailyInAWeekData(
    sensorId: string | number,
    week: number,
    year: number,
    classification: string,
    sensorType: string = SensorTypes.TEMPERATURE
  ): Observable<OrderedSensorPropertiesReadings> {
    const filters = `weekView: { year: ${year}, week: ${week}, offset_UTC: "Europe/Berlin"}`;
    const days: moment.Moment[] = [];
    const startDate = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek').toDate();

    for (let a = 0; a < 7; a++) {
      days.push(moment(startDate).add(a, 'days'));
    }

    const datasetLabels = this.helper.getDataSetLabels(sensorType, classification);
    const properties = Object.getOwnPropertyNames(datasetLabels);

    return this.getSensorReadings(sensorId, filters, sensorType, classification).pipe(
      map((resp: OrderedSensorPropertiesReadings): OrderedSensorPropertiesReadings => {
        const readings = resp.data.aggregateSensors.sensors;

        days.forEach((day: moment.Moment) => {
          if (isNil(readings.find((reading: IoTReadings) => reading.date === moment(day.toDate()).format(ISODateTimeFormat)))) {
            const reading = clone(first(readings))!;
            reading.date = moment(day.toDate()).format(ISODateTimeFormat);

            properties.forEach((prop: string) => {
              reading[prop] = null;
            });

            if (
              StringUtility.areStringsIdentical(sensorType, SensorTypes.SPEED) &&
              StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT)
            ) {
              reading.cars_zone_7_count = null;
              reading.cars_zone_30_count = null;
              reading.cars_zone_50_count = null;
              reading.cars_zone_60_count = null;
              reading.cars_zone_70_count = null;
              reading.cars_zone_80_count = null;
            }

            readings.push(reading);
          }
        });
        resp.data.aggregateSensors.sensors = orderBy(readings, (reading: IoTReadings) => moment(reading.date).toDate());
        return resp;
      })
    );
  }

  public getWeeklyDataByFifteenMinuteInterval(
    sensorId: string,
    _week: number,
    _year: number,
    sensorType: string = SensorTypes.DOOR,
    startDate: Date,
    sensorChartType: string = SensorDataChartTypes.fifteenMinute
  ): Observable<any[]> {
    const filters = `dayView: { date: "${moment(startDate).format(ExtendedDateFormat)}" , offset_UTC: "Europe/Berlin"}`;
    const days: moment.Moment[] = [];
    const intervals =
      sensorChartType === SensorDataChartTypes.fifteenMinute ? this.viertelStundenwerteService.getTimeInterval() : this.stundenwerteService.getTimeInterval();

    for (let a = 6; a >= 0; a--) {
      days.push(moment(startDate).subtract(a, 'days'));
    }

    const datasetLabels = this.helper.getDataSetLabels(sensorType);
    const properties = Object.getOwnPropertyNames(datasetLabels);

    const response: any[] = [];
    return this.getSensorReadings(sensorId, filters, sensorType).pipe(
      map((resp: OrderedSensorPropertiesReadings): any[] => {
        const readings = this.convertReadingsToLocalTime(resp.data.aggregateSensors.sensors);
        intervals.forEach((interval: string) => {
          const time = interval.split(':');
          const minuteReading: { [key: string]: any } = { time: interval };
          let index = 0;
          days.forEach((day: moment.Moment) => {
            const dailyUtc = `${day.format('YYYY-MM-DD')}T${interval}:00Z`;
            const reading = readings.find((read: any) => read.date === dailyUtc);
            const prop = `_${index}`;
            const dateInProcess = moment(day.startOf('day')).add(Number(time[0]), 'h').add(Number(time[1]), 'm');
            const todaysLocalTime = momenttz.tz(Date.now(), environment.languageTimezone).format('YYYY-MM-DD[T]HH:mm:ss');
            if (!isNil(reading)) {
              minuteReading[prop] = this.helper.getSensorReadingGeneral(sensorType, reading[first(properties) as string]);
            } else if (isNil(reading) && dateInProcess.isSameOrBefore(todaysLocalTime)) {
              minuteReading[prop] = 'negative';
            } else {
              minuteReading[prop] = null;
            }
            index++;
          });
          response.push(minuteReading);
        });

        return response;
      })
    );
  }

  public getYearlyData(
    sensorId: string | number,
    startYear: number,
    endYear: number,
    classification: string,
    sensorType: string = SensorTypes.TEMPERATURE
  ): Observable<OrderedSensorPropertiesReadings> {
    const filters = `yearView :{ startYear:${startYear}, endYear:${endYear}, offset_UTC: "Europe/Berlin"}`;

    const datasetLabels = this.helper.getDataSetLabels(sensorType, classification);
    const properties = Object.getOwnPropertyNames(datasetLabels);
    const years = range(startYear, endYear + 1);

    return this.getSensorReadings(sensorId, filters, sensorType, classification).pipe(
      map((resp: OrderedSensorPropertiesReadings): OrderedSensorPropertiesReadings => {
        const readings = resp.data.aggregateSensors.sensors;
        years.forEach((year: number) => {
          if (isNil(readings.find((reading: IoTReadings) => reading.year === year))) {
            const reading = clone(first(readings))!;
            reading.year = year;

            properties.forEach((prop: string) => {
              reading[prop] = null;
            });

            if (
              StringUtility.areStringsIdentical(sensorType, SensorTypes.SPEED) &&
              StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT)
            ) {
              reading.cars_zone_7_count = null;
              reading.cars_zone_30_count = null;
              reading.cars_zone_50_count = null;
              reading.cars_zone_60_count = null;
              reading.cars_zone_70_count = null;
              reading.cars_zone_80_count = null;
            }

            readings.push(reading);
          }
        });
        resp.data.aggregateSensors.sensors = orderBy(readings, (reading: IoTReadings) => reading.year);
        return resp;
      })
    );
  }

  public getSensorReadingsFromYear(
    sensorId: string | number,
    year: number,
    dateProperty: string = 'date',
    order: 'asc' | 'desc' = 'asc',
    sensorType: string = SensorTypes.TEMPERATURE
  ): Observable<SensorReadingsFromYear[]> {
    const filters = `yearEventView:${year}`;
    return this.getSensorReadings(sensorId, filters, sensorType).pipe(
      map((resp: OrderedSensorPropertiesReadings): SensorReadingsFromYear[] => {
        const readings = chain(resp.data.aggregateSensors.sensors)
          .orderBy((reading: IoTReadings) => reading[dateProperty], order)
          .map((reading: IoTReadings) => {
            const localDate = momenttz.tz(reading[dateProperty], environment.languageTimezone).format('HH:mm DD.MM.YYYY');
            return assign({}, reading, {
              [dateProperty]: localDate,
            });
          })
          .value();
        return _map(range(1, 13), (month: number) => {
          const foundReadingsPerMonth = readings.filter((reading: IoTReadings) => Number((reading[dateProperty] as string).split('.')[1]) === month);
          return {
            month,
            readings: foundReadingsPerMonth,
          };
        });
      })
    );
  }

  public getSensors(sensorType: string, municipalityId: string): Observable<SensorFilter<SensorTableNodes[]>> {
    let sensorInterfaces = '';
    let sensorProperties = '';
    if (StringUtility.hasAnyMatchInArray(PrimarySensorTypes, [sensorType?.toUpperCase()])) {
      sensorInterfaces = this.helper.getSensorSettingsInterfaces(sensorType);
      sensorProperties = this.helper.getSensorSettingsProperties(sensorType);
    }

    const readingCurrentValue =
      !isEmpty(sensorProperties) && !isNil(sensorProperties)
        ? `reading(currentValue: true) {
        date
        ${sensorProperties}
      }`
        : '';

    const query = GqlQueries.getSensorsPerDeviceTypeQuery(municipalityId, sensorType, sensorInterfaces, readingCurrentValue);
    return this.executeQuery<SensorFilter<SensorReadingOverviewNodes[]>>(query).pipe(
      switchMap((response: SensorFilter<SensorReadingOverviewNodes[]>) => {
        let nodes: SensorTableNodes[] = [];

        if (!isNil(response.data) && !isEmpty(response.data)) {
          nodes = response.data.sensorFilter.nodes.map((node: SensorReadingOverviewNodes) => {
            const ruleJSON =
              StringUtility.areStringsIdentical(node.sensorType, SensorTypes.TEMPERATURE) && node.alarmRules ? JSON.parse(node.alarmRules.ruleJSON) : null;

            const currentQuality =
              StringUtility.areStringsIdentical(node.sensorType, SensorTypes.CARBONDIOXIDE) && node.currentQuality.startsWith('{')
                ? JSON.parse(node.currentQuality)
                : null;

            const criticalQualityIndex =
              StringUtility.areStringsIdentical(node.sensorType, SensorTypes.CARBONDIOXIDE) && node.criticalQualityIndex.startsWith('{')
                ? JSON.parse(node.criticalQualityIndex)
                : null;

            const hasQualityIndices = !isNil(node.qualityIndices) && node.qualityIndices.length > 0;
            let qualityIndices: any[] = [];
            if (StringUtility.areStringsIdentical(node.sensorType, SensorTypes.CARBONDIOXIDE) && hasQualityIndices) {
              const parsedQualityIndices = JSON.parse(node.qualityIndices);
              const orderedQualityIndices = orderBy(parsedQualityIndices, (index) => index.Order, ['desc']);
              qualityIndices = orderedQualityIndices;
            } else {
              qualityIndices = [];
            }

            const sensorTableNodes: SensorTableNodes = {
              alarmRuleEnabled: node.alarmRuleEnabled,
              buildingType: node.buildingType,
              criticalQualityIndex: criticalQualityIndex,
              currentQuality: currentQuality,
              deviceEUI: node.deviceEUI,
              deviceID: node.deviceID,
              qualityIndices: qualityIndices,
              reading: node.reading ? node.reading[0] : null,
              ruleJSON: ruleJSON,
              sensorID: node.sensorID,
              sensorType: node.sensorType,
            };

            return sensorTableNodes;
          });
        }

        return of({
          data: {
            sensorFilter: {
              nodes: nodes,
            },
          },
        });
      }),
      tap((sensorDetails: SensorFilter<SensorTableNodes[]>) => {
        const buildingTypes: FilteredBuildingTypes[] = uniq(sensorDetails.data.sensorFilter.nodes.map((node: SensorTableNodes) => node.buildingType))
          .filter((buildingType) => buildingType)
          .map((uniqFilteredBuildingType) => ({
            name: uniqFilteredBuildingType,
          }));
        this._filteredBuildingTypes.next(buildingTypes);
      })
    );
  }

  public getSensorsByMunicipality(municipalityId: string = '09376160'): Observable<SensorFilter<TransformedNode[]>> {
    const query = GqlQueries.getSensorsByMunicipalityQuery(municipalityId);
    return this.executeQuery<SensorFilter<SensorsByMunicipalityNodes[]>>(query).pipe(
      map((res: SensorFilter<SensorsByMunicipalityNodes[]>): SensorFilter<TransformedNode[]> => {
        return {
          data: {
            sensorFilter: {
              nodes: res.data.sensorFilter.nodes.map((node: SensorsByMunicipalityNodes) => this.transformNode(node)),
            },
          },
        };
      })
    );
  }

  private transformNode(a: SensorsByMunicipalityNodes): TransformedNode {
    const currentQuality =
      StringUtility.areStringsIdentical(a.sensor_type, SensorTypes.CARBONDIOXIDE) && a.currentQuality.startsWith('{') ? JSON.parse(a.currentQuality) : null;

    const location = {
      ZIP: a.zip,
      buildingType: a.buildingType,
      street: a.street,
      district: a.district,
      name: a.name,
      longitude: a.b_longitude ?? a.longitude,
      latitude: a.b_latitude ?? a.latitude,
      houseNumber: a.houseNumber,
    };

    const ruleJSON = StringUtility.areStringsIdentical(a.sensor_type, SensorTypes.TEMPERATURE) && a.alarmRules ? JSON.parse(a.alarmRules.ruleJSON) : null;

    const node: TransformedNode = {
      alarmRuleEnabled: a.alarmRuleEnabled,
      buildingID: a.buildingID,
      currentQuality: currentQuality,
      device_eui: a.device_eui,
      device_id: a.device_id,
      device_name: a.display_type,
      location: location,
      locationContext: a.locationContext,
      reading: a.reading,
      ruleJSON: ruleJSON,
      sensor_id: a.sensor_id,
      sensor_type: a.sensor_type,
    };

    return node;
  }

  private getSensorReadings(
    sensorId: string | number,
    readingFilters: string,
    sensorType: string,
    classification: string = SensorClassification.NONE
  ): Observable<OrderedSensorPropertiesReadings> {
    const sensorProperties = this.helper.getSensorSettingsDetailsProperties(sensorType, classification);
    const query = GqlQueries.getSensorReadingsQuery(sensorId.toString(), readingFilters, sensorProperties);
    return this.executeQuery<SensorPropertiesReadings>(query).pipe(
      map((response: SensorPropertiesReadings): OrderedSensorPropertiesReadings => {
        return {
          data: {
            aggregateSensors: {
              sensors: orderBy(response.data.sensorByID.reading, 'date'),
            },
          },
        };
      })
    );
  }

  private convertReadingsToLocalTime(readings: IoTReadings[]): IoTReadings[] {
    return readings.map((reading: IoTReadings) => {
      const utc_time = momenttz.utc(reading.date, ISODateTimeFormat);
      const local_time = utc_time.clone();
      reading['date'] = `${local_time.format(ISODateTimeFormat)}`;
      return reading;
    });
  }

  public getSensorByDeviceId(municipalityKey: string, deviceId: string): Observable<Devices[]> {
    const sensorUrl = `socrates/administration/api/municipalities/${municipalityKey}/devices/${deviceId}`;

    return this.http.get<Devices[]>(this.endpoint(sensorUrl));
  }

  public getLocationContextByDeviceId(deviceId: string): Observable<string> {
    const query = GqlQueries.getLocationContextByDeviceIdQuery(deviceId);
    return this.executeQuery<SensorByIDLocationContext>(query).pipe(
      map((response: SensorByIDLocationContext): string => response?.data?.sensorByID?.locationContext || '')
    );
  }

  public getSensorDetailsBySensorId(sensorId: string, sensorType: string, classification: string): Observable<Sensor> {
    this.sensorMetaData = null;
    let sensorInterfaces = '';
    let sensorProperties = '';
    if (StringUtility.hasAnyMatchInArray(PrimarySensorTypes, [sensorType?.toUpperCase()])) {
      sensorInterfaces = this.helper.getSensorSettingsInterfaces(sensorType);
      sensorProperties = this.helper.getSensorSettingsProperties(sensorType);
    }

    const readingCurrentValue =
      !isEmpty(sensorProperties) && !isNil(sensorProperties)
        ? `reading(currentValue: true) {
        date
        ${sensorProperties}
      }`
        : '';

    const query = GqlQueries.getSensorDetailsBySensorIdQuery(sensorId, sensorInterfaces, readingCurrentValue);

    return this.executeQuery<SensorDetailsBySensorId>(query).pipe(
      map((res: SensorDetailsBySensorId): Sensor => this.generateMetaData(res)),
      switchMap((sensorMetaData: Sensor) => {
        if (
          StringUtility.areStringsIdentical(sensorMetaData.metaData.sensorType, SensorTypes.SPEED) &&
          StringUtility.areStringsIdentical(classification, SpeedSensorClassification.SPEED_LIMIT)
        ) {
          return this.getSpeedLimit(this.userMunicipalityService.currentMunicipality.guid, sensorId);
        } else {
          return of(null);
        }
      }),
      map((speedLimitResponse: Thresholds[] | null): Sensor => {
        if (!isNil(speedLimitResponse)) {
          if (!isNil(this.sensorMetaData)) {
            const speedLimit: SpeedLimitDto = !isEmpty(speedLimitResponse)
              ? {
                  value: speedLimitResponse[0].value,
                  isDefault: false,
                }
              : defaultSpeedLimit;
            this.sensorMetaData.metaData = { ...this.sensorMetaData.metaData, speedLimitJSON: speedLimit };
          }
        }

        return this.sensorMetaData as Sensor;
      })
    );
  }

  private generateMetaData(res: SensorDetailsBySensorId): Sensor {
    const data = res.data.sensorByID;
    const address = `${data.street || ''} ${data.houseNumber || ''}\r\n${data.zip || ''} ${data.district || ''}`;
    let qualityIndices: any[] = [];
    const hasQualityIndices = !isNil(data.qualityIndices) && data.qualityIndices.length > 0;
    if (StringUtility.areStringsIdentical(data.sensor_type, SensorTypes.CARBONDIOXIDE) && hasQualityIndices) {
      const parsedQualityIndices = JSON.parse(data.qualityIndices);
      const orderedQualityIndices = orderBy(parsedQualityIndices, (index) => index.Order, ['desc']);
      qualityIndices = orderedQualityIndices;
    } else {
      qualityIndices = [];
    }

    const reading: SensorReading = data.reading ? data.reading[0] : ({} as SensorReading);

    const metadata: TableMetadata = {
      address: address,
      alarmRuleEnabled: data.alarmsRuleEnabled,
      buildingType: data.buildingType,
      deviceEUI: data.device_eui,
      installationDate: data.installation_date,
      latitude: data.b_latitude ?? data.latitude,
      longitude: data.b_longitude ?? data.longitude,
      locationContext: data.location_context,
      sensorID: data.sensor_id,
      sensorType: data.sensor_type,
      readingData: reading,
      ruleJSON: StringUtility.areStringsIdentical(data.sensor_type, SensorTypes.TEMPERATURE) && data.alarmRules ? JSON.parse(data.alarmRules.ruleJSON) : null,
      currentQuality:
        StringUtility.areStringsIdentical(data.sensor_type, SensorTypes.CARBONDIOXIDE) && data.currentQuality.startsWith('{')
          ? JSON.parse(data.currentQuality)
          : null,
      criticalQualityIndex:
        StringUtility.areStringsIdentical(data.sensor_type, SensorTypes.CARBONDIOXIDE) && data.criticalQualityIndex.startsWith('{')
          ? JSON.parse(data.criticalQualityIndex)
          : null,
      qualityIndices: qualityIndices,
      speedLimitJSON: {} as SpeedLimitDto,
    };

    this.sensorMetaData = { metaData: metadata };

    return this.sensorMetaData;
  }

  public getDeviceInfo(
    municipalityKey: string,
    sensorType: string,
    itemsPerPage: number = 7,
    pageNumber: number = 1,
    sortBy: string = 'onboardingKey',
    sortOrder: string = '',
    selectedBuildingType: any[] = [],
    searchTerm: string = ''
  ): Observable<Sensor[]> {
    sensorType = sensorType.toLowerCase();
    let pagination = ` &$top=${itemsPerPage}&$skip=0`;
    let searchQuery = '';
    let buildingTypeCollection = '';
    let buildingTypeQuery = '';

    if (pageNumber > 1) {
      pagination = ` &$top=${itemsPerPage}&$skip=${(pageNumber - 1) * itemsPerPage}`;
    }

    if (!itemsPerPage) {
      pagination = '';
    }

    if (selectedBuildingType.length > 0) {
      buildingTypeCollection = selectedBuildingType.map((selected) => `'${selected.name}'`).join();
      buildingTypeQuery = ` and buildingType in (${buildingTypeCollection})`;
    }

    if (!!searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      searchQuery = ` and (contains(toLower(onboardingKey),'${searchTerm}') or contains(toLower(street),'${searchTerm}') or contains(toLower(houseNumber),'${searchTerm}') or contains(toLower(zipCode),'${searchTerm}') or contains(toLower(district),'${searchTerm}') or contains(toLower(buildingType),'${searchTerm}') or contains(toLower(locationContext),'${searchTerm}'))`;
    }

    return this.http
      .get<DevicesWithCountTrue>(
        this.endpoint(
          `socrates/administration/api/municipalities/${municipalityKey}/devices?$filter=isActive eq true ${buildingTypeQuery} and toLower(typeName) eq '${sensorType}'${searchQuery}${pagination}&$count=true&$orderby=${sortBy} ${sortOrder}`
        )
      )
      .pipe(
        map((response: DevicesWithCountTrue): Sensor[] => {
          return response.data.map((sensor: Devices): Sensor => {
            return {
              metaData: {
                buildingType: sensor.buildingType ? sensor.buildingType : '',
                deviceEUI: sensor.onboardingKey,
                sensorID: sensor.deviceId,
                locationContext: sensor.locationContext,
                installationDate: sensor.onBoardingDate,
                address: sensor.buildingId ? sensor.street.concat(' ', sensor.houseNumber || '', '\r\n', sensor.zipCode || '', ' ', sensor.district || '') : '',
                longitude: sensor.locationLongitude,
                latitude: sensor.locationLatitude,
                buildingId: sensor.buildingId ? sensor.buildingId : null,
                sensorType: sensor.deviceTypeName,
              },
              pageInfo: {
                page: pageNumber,
                totalPages: response.maxRowCount,
              },
            } as Sensor;
          });
        })
      );
  }

  public getSpeedLimit(municipalityGuid: string, deviceId: string): Observable<Thresholds[]> {
    return this.http.get<Thresholds[]>(
      this.endpoint(`socrates/administration/api/municipalities/${municipalityGuid}/Devices/${deviceId}/Thresholds?$Filter=measurementTypeName eq 'speed_1'`)
    );
  }

  public setSpeedLimit(municipalityGuid: string, deviceId: string, speedLimit: number): Observable<Thresholds> {
    const speedLimitRequest: Record<string, string | number> = {
      value: speedLimit,
    };

    return this.getSpeedLimit(municipalityGuid, deviceId).pipe(
      switchMap((res: Thresholds[]) => {
        if (isNil(res) || res.length === 0) {
          speedLimitRequest['measurementTypeName'] = 'speed_1';
          return this.http.post<Thresholds>(
            this.endpoint(`socrates/administration/api/municipalities/${municipalityGuid}/Devices/${deviceId}/Thresholds`),
            speedLimitRequest
          );
        } else {
          return this.http.patch<Thresholds>(
            this.endpoint(`socrates/administration/api/municipalities/${municipalityGuid}/Devices/${deviceId}/Thresholds/${res[0].thresholdId}`),
            speedLimitRequest
          );
        }
      })
    );
  }

  public getCO2Threshold(municipalityGuid: string, deviceId: string): Observable<Thresholds[]> {
    return this.http.get<Thresholds[]>(
      this.endpoint(
        `socrates/administration/api/municipalities/${municipalityGuid}/Devices/${deviceId}/Thresholds?$Filter=measurementTypeName eq 'CO2_1'&$orderby=sortOrder desc`
      )
    );
  }

  public onboardSetThreshold(municipalityGuid: string, deviceId: string, indices: QualityIndex[]): Observable<Thresholds[]> {
    const defaultIndices: QualityIndexDto[] = indices.map((index: QualityIndex) => ({
      colourHex: index.color,
      value: index.value,
      description: index.text,
      sortOrder: index.order,
    }));

    const defaultThresholds$ = defaultIndices.map((qIndex: QualityIndexDto) => {
      qIndex['MeasurementTypeName'] = 'CO2_1';
      return this.http
        .post<Thresholds>(this.endpoint(`socrates/administration/api/municipalities/${municipalityGuid}/Devices/${deviceId}/Thresholds`), qIndex)
        .pipe(catchError(() => of({} as Thresholds)));
    });

    return forkJoin(defaultThresholds$);
  }

  public setThreshold(municipalityGuid: string, deviceId: string, indices: QualityIndex[]): Observable<Thresholds[]> {
    if (StringUtility.areStringsIdentical(this.sensorTypeDataStoreService.selectedSensorType?.code as string, SensorTypes.CARBONDIOXIDE)) {
      return this.getCO2Threshold(municipalityGuid, deviceId).pipe(
        switchMap((res: Thresholds[]) => {
          const qualityIndeces: QualityIndexDto[] = indices.map((index: QualityIndex) => ({
            colourHex: index.color,
            value: index.value,
            description: index.text,
            sortOrder: index.order,
          }));

          const thresholds$ = qualityIndeces.map((qIndex: QualityIndexDto, index: number) => {
            if (isNil(res) || res.length === 0) {
              qIndex['MeasurementTypeName'] = 'CO2_1';
              return this.http
                .post<Thresholds>(this.endpoint(`socrates/administration/api/municipalities/${municipalityGuid}/Devices/${deviceId}/Thresholds`), qIndex)
                .pipe(catchError(() => of({} as Thresholds)));
            } else {
              const thresholdId = res[index].thresholdId;
              return this.http
                .patch<Thresholds>(
                  this.endpoint(`socrates/administration/api/municipalities/${municipalityGuid}/Devices/${deviceId}/Thresholds/${thresholdId}`),
                  qIndex
                )
                .pipe(catchError(() => of({} as Thresholds)));
            }
          });

          return forkJoin(thresholds$);
        })
      );
    } else {
      return of([]);
    }
  }

  public getMeasurementTypes(sensorType: string): Observable<DeviceTypeMeasurementTypeMappers[]> {
    const baseUri = 'socrates/administration/api/references/devicetypes?';
    return this.http
      .get<DeviceTypes[]>(
        this.endpoint(
          `${baseUri}$filter=typeName eq '${sensorType}'&$select=DeviceTypeMeasurementTypeMappers&$expand=DeviceTypeMeasurementTypeMappers($expand=MeasurementTypeNameNavigation;$select=measurementTypeName,deviceType)`
        )
      )
      .pipe(
        map((response: DeviceTypes[]): DeviceTypeMeasurementTypeMappers[] =>
          flattenDeep(response.map((deviceTypes: DeviceTypes) => deviceTypes.deviceTypeMeasurementTypeMappers))
        ),
        map((measurementType: DeviceTypeMeasurementTypeMappers[]): DeviceTypeMeasurementTypeMappers[] => {
          const orderMeasurementTypes = orderBy(measurementType, (data) => data.measurementTypeNameNavigation.displayName, ['asc']);
          const measurementTypeFilter = orderMeasurementTypes.filter(
            (navi: DeviceTypeMeasurementTypeMappers) => navi.measurementTypeNameNavigation?.unit !== MeasurementTypes.binary
          );
          return measurementTypeFilter;
        })
      );
  }

  public fetchSpanView(state: SensorReadingState): Observable<LibChart> {
    const query = GqlQueries.getSpanViewQuery(state.sensorId, state.startDateStr, state.endDateStr, state.granularity, state.measurementType);
    return this.executeQuery<SpanView>(query).pipe(
      map((resp: SpanView): LibChart => {
        if (!resp?.data?.sensorByID?.reading?.length) {
          return {} as LibChart;
        }
        const respData = resp.data.sensorByID.reading;
        return SensorDataUtility.generateChartDetails(respData, state);
      })
    );
  }
}
